import React, { useState, useMemo } from 'react';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import Plus from 'lucide-react/dist/esm/icons/plus';
import PanelLeftClose from 'lucide-react/dist/esm/icons/panel-left-close';
import PanelLeft from 'lucide-react/dist/esm/icons/panel-left';
import FolderOpen from 'lucide-react/dist/esm/icons/folder-open';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import Search from 'lucide-react/dist/esm/icons/search';
import X from 'lucide-react/dist/esm/icons/x';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import { getTranslation } from '../../../i18n';

// Hover preview card
const AnalysisPreview = ({ analysis, isVisible }) => {
  if (!isVisible || !analysis) return null;
  
  return (
    <div className="absolute left-full top-0 ml-2 z-tooltip w-[220px] p-3 bg-surface-page border border-status-success/30 rounded-xl shadow-2xl opacity-0 scale-95 pointer-events-none animate-[fadeIn_0.15s_ease-out_forwards]">
      <style>{`
        @keyframes fadeIn {
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-default">
        <Building2 size={12} className="text-status-success" />
        <span className="text-xs font-semibold text-content truncate">{analysis.company_name}</span>
        {analysis.fiscal_years_display && (
          <span className="text-[10px] px-1.5 py-0.5 bg-brand/20 text-primary rounded-md font-medium">
            {analysis.fiscal_years_display}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2 text-[10px] text-content-secondary">
          <FileText size={10} className="text-content-muted" />
          <span className="truncate">{analysis.document_name || 'Document'}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-content-secondary">
          <Calendar size={10} className="text-content-muted" />
          <span>{new Date(analysis.completed_at || analysis.created_at).toLocaleDateString()}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <CheckCircle size={10} className="text-status-success" />
          <span className="text-status-success capitalize">{analysis.status}</span>
        </div>
      </div>
    </div>
  );
};

const AnalysisSidebar = ({ 
  isOpen, 
  onToggle, 
  analyses, 
  onSelectAnalysis, 
  onNewAnalysis,
  onDeleteAnalysis,
  loading, 
  currentAnalysisId,
  language = 'en'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredAnalysisId, setHoveredAnalysisId] = useState(null);
  const t = (key) => getTranslation(language, key);

  const filteredAnalyses = useMemo(() => {
    if (!analyses || !searchQuery.trim()) return analyses || [];
    const query = searchQuery.toLowerCase().trim();
    return analyses.filter(analysis => {
      const companyName = (analysis.company_name || '').toLowerCase();
      const fiscalYears = (analysis.fiscal_years_display || '').toLowerCase();
      const date = analysis.completed_at || analysis.created_at || '';
      return companyName.includes(query) || fiscalYears.includes(query) || date.includes(query);
    });
  }, [analyses, searchQuery]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      else if (diffDays === 1) return 'Yesterday';
      else if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
      else return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <>
      {/* Toggle Button - shows when sidebar is closed */}
      <button 
        className={`fixed top-[75px] left-[68px] w-[42px] h-[42px] bg-surface-page border border-status-success/30 rounded-xl text-status-success cursor-pointer items-center justify-center z-[60] transition-all duration-200 shadow-[0_4px_16px_rgba(0,0,0,0.3)] hover:bg-status-success/15 hover:border-status-success/50 hover:translate-x-0.5 hover:shadow-[0_6px_24px_rgba(16,185,129,0.3)] ${!isOpen ? 'flex' : 'hidden'}`}
        onClick={onToggle}
        title={t('openHistory')}
      >
        <PanelLeft size={20} />
      </button>

      {/* Sidebar */}
      <aside 
        className={`fixed top-[60px] left-[60px] w-[240px] h-[calc(100vh-60px)] bg-surface-page border-r border-subtle z-[50] flex flex-col transition-all duration-300 ${isOpen ? '' : '-translate-x-[240px]'}`}
        data-walkthrough="sidebar"
      >
        {/* Header with integrated collapse button */}
        <div className="relative flex items-center py-2.5 px-3 border-b border-subtle">
          <div className="flex items-center gap-2 text-content">
            <FolderOpen size={14} className="text-status-success" />
            <h2 className="text-[0.7rem] font-semibold m-0 tracking-widest uppercase opacity-80">{t('history')}</h2>
          </div>
          
          {/* Collapse button - positioned within header */}
          <button 
            className={`absolute right-2 flex items-center justify-center w-7 h-7 bg-surface-primary border border-default rounded-lg text-content-tertiary cursor-pointer z-[60] transition-all duration-200 hover:bg-status-success/15 hover:border-status-success/40 hover:text-status-success ${!isOpen ? 'hidden' : ''}`}
            onClick={onToggle}
            title={t('collapseHistory')}
          >
            <PanelLeftClose size={14} />
          </button>
        </div>

        {/* New Analysis Button */}
        <button 
          className="flex items-center justify-center gap-1.5 mx-2.5 my-2 py-2 px-3 bg-status-success/10 border border-dashed border-status-success/30 rounded-lg text-status-success text-[0.7rem] font-semibold cursor-pointer transition-all duration-200 hover:bg-status-success/20 hover:border-status-success/50"
          onClick={onNewAnalysis}
        >
          <Plus size={12} />
          <span>{t('newAnalysis')}</span>
        </button>

        {/* Search Input */}
        {analyses && analyses.length > 0 && (
          <div 
            className="relative flex items-center gap-1.5 mx-2.5 mb-2 py-1.5 px-2 bg-surface-primary border border-default rounded-md transition-all duration-200 focus-within:border-status-success/40 focus-within:bg-surface-primary"
            data-walkthrough="search"
          >
            <Search size={11} className="text-content-muted flex-shrink-0" />
            <input
              type="text"
              placeholder={t('searchAnalyses')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-none text-content text-[0.7rem] outline-none placeholder:text-content-muted"
            />
            {searchQuery && (
              <button 
                className="flex items-center justify-center w-3.5 h-3.5 bg-surface-elevated border-none rounded-full text-content-tertiary cursor-pointer transition-all duration-200 hover:bg-surface-elevated hover:text-content"
                onClick={() => setSearchQuery('')}
              >
                <X size={8} />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-1.5 py-1.5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-7 h-7 border-2 border-default border-t-success rounded-full animate-spin mb-3" />
              <p className="text-content-muted text-[0.8rem] m-0">Loading...</p>
            </div>
          ) : !analyses || analyses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <div className="w-14 h-14 rounded-[14px] bg-surface-primary border border-dashed border-default flex items-center justify-center text-content-faint mb-4">
                <FileText size={24} />
              </div>
              <h3 className="text-content-secondary text-[0.9rem] font-semibold m-0 mb-1.5">{t('noAnalysesYet')}</h3>
              <p className="text-content-muted text-xs m-0 leading-relaxed">{t('uploadToStart')}</p>
            </div>
          ) : filteredAnalyses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <div className="w-14 h-14 rounded-[14px] bg-surface-primary border border-dashed border-default flex items-center justify-center text-content-faint mb-4">
                <Search size={24} />
              </div>
              <h3 className="text-content-secondary text-[0.9rem] font-semibold m-0 mb-1.5">{t('noResults')}</h3>
              <p className="text-content-muted text-xs m-0 leading-relaxed">{t('tryDifferentSearch')}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-1.5 py-1 px-2 mb-0.5 text-content-muted text-[0.6rem] font-semibold uppercase tracking-widest">
                <span>{t('recentAnalyses')}</span>
                {searchQuery && (
                  <span className="text-status-success/80 text-[0.6rem] font-medium">{filteredAnalyses.length} {t('found')}</span>
                )}
              </div>
              <div className="flex flex-col gap-px">
                {filteredAnalyses.map((analysis) => (
                  <div
                    key={analysis.analysis_id}
                    className={`flex items-center relative rounded-md transition-all duration-200 w-full border-l-2 group ${
                      currentAnalysisId === analysis.analysis_id 
                        ? 'bg-gradient-to-r from-status-success/15 to-brand/[0.08] border-l-success' 
                        : 'bg-transparent border-l-transparent hover:bg-surface-primary hover:border-l-success/20'
                    }`}
                    onMouseEnter={() => setHoveredAnalysisId(analysis.analysis_id)}
                    onMouseLeave={() => setHoveredAnalysisId(null)}
                  >
                    <button
                      className="flex items-center gap-2 py-1.5 pl-2 pr-7 bg-transparent border-none cursor-pointer text-left w-full transition-all duration-200 btn-bounce"
                      onClick={() => onSelectAnalysis(analysis.analysis_id)}
                    >
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110 ${
                        currentAnalysisId === analysis.analysis_id 
                          ? 'bg-status-success/25 text-status-success' 
                          : 'bg-status-success/[0.12] text-status-success'
                      }`}>
                        <FileText size={11} />
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <h3 className={`text-[0.7rem] font-medium m-0 mb-0.5 whitespace-nowrap overflow-hidden text-ellipsis ${
                          currentAnalysisId === analysis.analysis_id ? 'text-status-success font-semibold' : 'text-content-secondary'
                        }`}>
                          {analysis.display_name || analysis.company_name || 'Unknown Company'}
                        </h3>
                        <div className="flex items-center gap-1 text-[0.6rem] text-content-muted">
                          <Calendar size={8} className="flex-shrink-0 opacity-60" />
                          <span>{formatDate(analysis.completed_at || analysis.created_at)}</span>
                          {/* Progress/Status indicator */}
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                            analysis.status === 'completed' ? 'bg-status-success shadow-[0_0_4px_rgba(16,185,129,0.5)]' :
                            analysis.status === 'processing' || analysis.status === 'pending' ? 'bg-status-warning animate-pulse' :
                            'bg-status-error'
                          }`} title={analysis.status} />
                        </div>
                      </div>
                    </button>
                    {onDeleteAnalysis && (
                      <button
                        className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center bg-transparent border-none rounded text-content-faint cursor-pointer opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-status-error/15 hover:text-status-error"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteAnalysis(analysis);
                        }}
                        title={t('deleteAnalysis')}
                      >
                        <Trash2 size={10} />
                      </button>
                    )}
                    {/* Hover Preview */}
                    <AnalysisPreview 
                      analysis={analysis} 
                      isVisible={hoveredAnalysisId === analysis.analysis_id && currentAnalysisId !== analysis.analysis_id}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="py-1.5 px-2.5 border-t border-subtle bg-black/20">
          <p className="m-0 text-[0.6rem] text-content-muted text-center">
            {searchQuery 
              ? `${filteredAnalyses.length} / ${analyses?.length || 0} ${t('analysesCount')}`
              : `${analyses?.length || 0} ${analyses?.length === 1 ? t('analysisCount') : t('analysesCount')}`
            }
          </p>
        </div>
      </aside>
    </>
  );
};

export default AnalysisSidebar;
